import { RESOURCES, type Resource, type ResourceAction, type RolePermissionEntry } from './types';

/**
 * PE-2147 — pure state for the permissions matrix modal: resource rows × action
 * checkboxes, serialised back to the `PUT /department-roles/:id/permissions`
 * shape (`[{ resource, actions }]`, one row per resource, no empty rows —
 * `actions` has `@ArrayMinSize(1)` server-side).
 */
export type MatrixState = Partial<Record<Resource, ResourceAction[]>>;

export const fromEntries = (entries: RolePermissionEntry[] | undefined): MatrixState => {
  const state: MatrixState = {};
  for (const entry of entries ?? []) {
    if (!entry.actions?.length) continue;
    state[entry.resource] = Array.from(new Set([...(state[entry.resource] ?? []), ...entry.actions]));
  }
  return state;
};

export const toEntries = (state: MatrixState): RolePermissionEntry[] =>
  RESOURCES.flatMap((resource) => {
    const actions = state[resource];
    return actions && actions.length ? [{ resource, actions: [...actions] }] : [];
  });

export const hasAction = (state: MatrixState, resource: Resource, action: ResourceAction): boolean =>
  (state[resource] ?? []).includes(action);

export const toggleAction = (state: MatrixState, resource: Resource, action: ResourceAction): MatrixState => {
  const current = state[resource] ?? [];
  const next = current.includes(action) ? current.filter((a) => a !== action) : [...current, action];
  const copy = { ...state };
  if (next.length) copy[resource] = next;
  else delete copy[resource];
  return copy;
};

/** Tick/untick every action on one resource row. */
export const setRow = (
  state: MatrixState,
  resource: Resource,
  actions: readonly ResourceAction[],
  on: boolean,
): MatrixState => {
  const copy = { ...state };
  if (on) copy[resource] = [...actions];
  else delete copy[resource];
  return copy;
};

export const countGranted = (state: MatrixState): number =>
  Object.values(state).reduce((sum, actions) => sum + (actions?.length ?? 0), 0);

/** Order-insensitive equality, used for the dirty flag. */
export const isSameMatrix = (a: MatrixState, b: MatrixState): boolean => {
  const norm = (s: MatrixState) =>
    JSON.stringify(toEntries(s).map((e) => ({ resource: e.resource, actions: [...e.actions].sort() })));
  return norm(a) === norm(b);
};
